// The live « new version » bar after « Régénérer » (owner, 2026-10-21).
//
// Two lines, both live:
//   • Aperçu 3D — the quick redraw of the changed pages (seconds), from the
//     worker's quick job (lib/quickJobs.ts) or, when no runner answered, from
//     the redraw this tab is running itself;
//   • PDF d'impression — the print rebuild (minutes), from orders.pdf_status.
// Polls /pdf/quick-status every second while anything is building, every 5 s
// otherwise, and stops polling once everything has settled.
import { useEffect, useRef, useState } from 'react';
import { ordersApi, type QuickJobView, type QuickStatusResponse } from '@/lib/api';
import { quickBarModel, type BarModel, type LocalQuick } from '@/lib/quickBuildModel';
import { useT } from '@/lib/i18n';

export default function QuickBuildProgress({
  shopifyOrderId,
  album,
  local,
  since,
  compact,
}: {
  shopifyOrderId: string;
  /** Only this album's quick job (null = every album). */
  album?: number | null;
  /** The in-tab fallback's own progress, when it is the one drawing. */
  local?: LocalQuick | null;
  /** Ignore jobs requested before this instant (an older click). */
  since?: number;
  compact?: boolean;
}) {
  const [st, setSt] = useState<QuickStatusResponse | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const t0 = useRef(Date.now());

  useEffect(() => {
    let dead = false;
    const tick = async () => {
      let next = 5000;
      try {
        const r = await ordersApi.quickStatus(shopifyOrderId);
        if (dead) return;
        setSt(r);
        const m = quickBarModel(r, { album: album ?? null, local: local ?? null, since: since ?? 0, nowMs: Date.now() });
        next = m.settled ? 0 : m.quick.active || m.print.active ? 1000 : 5000;
      } catch { next = 5000; }
      if (!dead && next > 0) timer.current = setTimeout(tick, next);
    };
    void tick();
    return () => { dead = true; if (timer.current) clearTimeout(timer.current); };
  }, [shopifyOrderId, album, since, local?.stage]);

  const { t } = useT();
  const m = quickBarModel(st, { album: album ?? null, local: local ?? null, since: since ?? 0, nowMs: Date.now() });
  const say = (b: BarModel) => t(b.tpl).replace(/\{(\w+)\}/g, (_, k: string) => String(b.vars[k] ?? ''));
  const elapsed = Math.round((Date.now() - t0.current) / 1000);
  return (
    <div style={{ display: 'grid', gap: compact ? 6 : 10, textAlign: 'left' }} aria-live="polite">
      <Bar label={t('Aperçu 3D')} pct={m.quick.pct} text={say(m.quick)} tone={m.quick.tone} />
      <Bar label={t('PDF d’impression')} pct={m.print.pct} text={say(m.print)} tone={m.print.tone} />
      {!compact && !m.settled && <div style={{ fontSize: 11, color: '#8A7F72' }}>{elapsed} s</div>}
    </div>
  );
}

function Bar({ label, pct, text, tone }: { label: string; pct: number; text: string; tone: 'run' | 'ok' | 'warn' | 'idle' }) {
  const color = tone === 'ok' ? '#2E7D4F' : tone === 'warn' ? '#B4561F' : tone === 'run' ? '#B9973A' : '#CFC6B8';
  return (
    <div>
      <div style={{ display: 'flex', justifyContent: 'space-between', fontSize: 12, color: '#3B2F22', marginBottom: 3 }}>
        <span style={{ fontWeight: 600 }}>{label}</span><span>{text}</span>
      </div>
      <div style={{ height: 6, background: '#EFE8DC', borderRadius: 3, overflow: 'hidden' }}>
        <div style={{ width: `${Math.max(0, Math.min(100, pct))}%`, height: '100%', background: color, transition: 'width .4s ease' }} />
      </div>
    </div>
  );
}

export type { QuickJobView };
